import React, { useState, useRef, useEffect } from 'react';
import { API_BASE } from "../config";
import { clientToSvg } from "../utils/svgUtils";

function DrillDetail({ drill, onBack, onSaveOverwrite, onSaveNew, onDelete }) {
  const [positions, setPositions] = useState(drill.positions || []);
  const [title, setTitle] = useState(drill.title || "");
  const [primaryObjectives, setPrimaryObjectives] = useState(drill.primary_objectives || []);
  const [secondaryObjectives, setSecondaryObjectives] = useState(drill.secondary_objectives || []);
  const [format, setFormat] = useState(drill.format || "");
  const [execution, setExecution] = useState(drill.execution || "");
  const [isEditingExecution, setIsEditingExecution] = useState(false);
  const [ageCategories, setAgeCategories] = useState(drill.age_categories || []);
  const [categories, setCategories] = useState(drill.categories || []);
  const [duration, setDuration] = useState(drill.duration_min || 0);
  const [complexity, setComplexity] = useState(drill.complexity || 1);
  const [balls, setBalls] = useState(drill.balls || []);

  const [miniGoals, setMiniGoals] = useState(drill.miniGoals || []);
  const [cones, setCones] = useState(drill.cones || []);
  const [markers, setMarkers] = useState(drill.markers || []);

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingFormat, setIsEditingFormat] = useState(false);

  // Constraints editing support
  const [constraints, setConstraints] = useState(drill.constraints || []);
  const [isEditingConstraints, setIsEditingConstraints] = useState(false);

  // Strengths & Weaknesses editing support
  const [strengths, setStrengths] = useState(drill.strengths || []);
  const [isEditingStrengths, setIsEditingStrengths] = useState(false);
  const [weaknesses, setWeaknesses] = useState(drill.weaknesses || []);
  const [isEditingWeaknesses, setIsEditingWeaknesses] = useState(false);

  // Goal Areas
  const [goalAreas, setGoalAreas] = useState(drill.goalAreas || []);

  const [saveMessage, setSaveMessage] = useState("");

  const [objectivesList, setObjectivesList] = useState([]);
  const [drillsList, setDrillsList] = useState([]);

  useEffect(() => {
    fetch(`${API_BASE}/objectives`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch objectives");
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data)) {
          setObjectivesList(data);
        } else {
          setObjectivesList(data.objectives || []);
        }
      })
      .catch((err) => {
        console.error("Error loading objectives:", err);
      });
    fetch(`${API_BASE}/drills.json`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch drills");
        return res.json();
      })
      .then((data) => setDrillsList(data))
      .catch((err) => {
        console.error("Error loading drills:", err);
      });
  }, []);

  const svgRef = useRef(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const draggingIndex = useRef(null);
  const draggingType = useRef(null); // "position" or "ball"

  const saveOverwrite = async () => {
    try {
      await onSaveOverwrite({
        ...drill,
        title,
        primary_objectives: primaryObjectives,
        secondary_objectives: secondaryObjectives,
        format,
        execution,
        age_categories: ageCategories,
        categories,
        duration_min: duration,
        complexity,
        positions,
        balls,
        miniGoals,
        cones,
        markers,
        constraints,
        strengths,
        weaknesses,
        goalAreas,
      });
      setSaveMessage("✅ Esercizio salvato con successo!");
      setTimeout(() => setSaveMessage(""), 3000);
    } catch (err) {
      console.error("Errore salvataggio:", err);
      setSaveMessage("❌ Errore nel salvataggio");
      setTimeout(() => setSaveMessage(""), 3000);
    }
  };

  const saveNew = () => {
    onSaveNew({
      ...drill,
      title,
      primary_objectives: primaryObjectives,
      secondary_objectives: secondaryObjectives,
      format,
      execution,
      age_categories: ageCategories,
      categories,
      duration_min: duration,
      complexity,
      positions,
      balls,
      miniGoals,
      cones,
      markers,
      constraints,
      strengths,
      weaknesses,
      goalAreas,
    });
  };

  const deleteDrill = async () => {
    if (onDelete) {
      try {
        await onDelete(drill);
        setSaveMessage("✅ Esercizio eliminato con successo!");
        setTimeout(() => setSaveMessage(""), 3000);
      } catch (err) {
        console.error("Errore eliminazione:", err);
        setSaveMessage("❌ Errore nell'eliminazione");
        setTimeout(() => setSaveMessage(""), 3000);
      }
    }
  };

  return (
    <div className="container">
      <button className="btn btn-secondary mb-3" onClick={onBack}>⬅ Torna</button>
      <div className="card p-3 mb-3">
        {/* Strengths (Punti di forza) */}
        <div className="mb-2">
          <label className="form-label"><strong>Punti di forza:</strong></label>
          {isEditingStrengths ? (
            <textarea
              className="form-control"
              rows="2"
              value={strengths.join("\n")}
              onChange={e => setStrengths(e.target.value.split("\n"))}
              onBlur={() => setIsEditingStrengths(false)}
              autoFocus
            />
          ) : (
            <div
              onDoubleClick={() => setIsEditingStrengths(true)}
              style={{ cursor: "pointer", whiteSpace: "pre-wrap" }}
            >
              {strengths.length > 0 && strengths.some(s => s.trim() !== "") ? strengths.join("\n") : "—"}
            </div>
          )}
        </div>
        {/* Weaknesses (Punti di debolezza) */}
        <div className="mb-2">
          <label className="form-label"><strong>Punti di debolezza:</strong></label>
          {isEditingWeaknesses ? (
            <textarea
              className="form-control"
              rows="2"
              value={weaknesses.join("\n")}
              onChange={e => setWeaknesses(e.target.value.split("\n"))}
              onBlur={() => setIsEditingWeaknesses(false)}
              autoFocus
            />
          ) : (
            <div
              onDoubleClick={() => setIsEditingWeaknesses(true)}
              style={{ cursor: "pointer", whiteSpace: "pre-wrap" }}
            >
              {weaknesses.length > 0 && weaknesses.some(w => w.trim() !== "") ? weaknesses.join("\n") : "—"}
            </div>
          )}
        </div>
        <div className="mb-2">
          <label className="form-label"><strong>Titolo: </strong></label>
          {isEditingTitle ? (
            <input
              className="form-control"
              value={title}
              onChange={e => setTitle(e.target.value)}
              onBlur={() => setIsEditingTitle(false)}
              autoFocus
            />
          ) : (
            <span onDoubleClick={() => setIsEditingTitle(true)} style={{ cursor: "pointer" }}>
              {title || "—"}
            </span>
          )}
        </div>
        <div className="mb-2">
          <label className="form-label"><strong>Obiettivi: </strong></label>
          <div>
            {objectivesList.map((obj, idx) => {
              const isPrimary = primaryObjectives.includes(obj);
              const isSecondary = secondaryObjectives.includes(obj);
              let backgroundColor = "#e0e0e0";
              let color = "black";
              if (isPrimary) {
                backgroundColor = "#007bff";
                color = "white";
              } else if (isSecondary) {
                backgroundColor = "#28a745";
                color = "white";
              }
              return (
                <span
                  key={`obj-${idx}`}
                  onClick={() => {
                    if (isPrimary) {
                      setPrimaryObjectives(primaryObjectives.filter(o => o !== obj));
                      setSecondaryObjectives([...secondaryObjectives, obj]);
                    } else if (isSecondary) {
                      setSecondaryObjectives(secondaryObjectives.filter(o => o !== obj));
                    } else {
                      setPrimaryObjectives([...primaryObjectives, obj]);
                    }
                  }}
                  style={{
                    display: "inline-block",
                    margin: "4px",
                    padding: "4px 8px",
                    borderRadius: "12px",
                    backgroundColor,
                    color,
                    cursor: "pointer"
                  }}
                >
                  {obj}
                </span>
              );
            })}
          </div>
        </div>
        <div className="mb-2">
          <label className="form-label"><strong>Formato: <br/> </strong></label>
          {isEditingFormat ? (
            <input
              className="form-control"
              value={format}
              onChange={e => setFormat(e.target.value)}
              onBlur={() => setIsEditingFormat(false)}
              autoFocus
            />
          ) : (
            <span onDoubleClick={() => setIsEditingFormat(true)} style={{ cursor: "pointer" }}>
              {format || "—"}
            </span>
          )}
        </div>
        <div className="mb-2">
          <label className="form-label"><strong>Esecuzione: </strong></label>
          {isEditingExecution ? (
            <textarea
              className="form-control"
              rows="3"
              value={execution}
              onChange={e => setExecution(e.target.value)}
              onBlur={() => setIsEditingExecution(false)}
              autoFocus
            />
          ) : (
            <div
              onDoubleClick={() => setIsEditingExecution(true)}
              style={{ cursor: "pointer", whiteSpace: "pre-wrap" }}
            >
              {execution || "—"}
            </div>
          )}
        </div>
        <div className="mb-2">
          <label className="form-label"><strong>Vincoli: </strong></label>
          {isEditingConstraints ? (
            <textarea
              className="form-control"
              rows="2"
              value={constraints.join("\n")}
              onChange={e => setConstraints(e.target.value.split("\n"))}
              onBlur={() => setIsEditingConstraints(false)}
              autoFocus
            />
          ) : (
            <div
              onDoubleClick={() => setIsEditingConstraints(true)}
              style={{ cursor: "pointer", whiteSpace: "pre-wrap" }}
            >
              {constraints.length > 0 ? constraints.join("\n") : "—"}
            </div>
          )}
        </div>
        <div className="mb-2">
          <label className="form-label"><strong>Categorie età:</strong></label>
          <div>
            {["U10", "U12", "U14", "U15", "Tutte"].map((cat, idx) => {
              const active = ageCategories.includes(cat);
              return (
                <span
                  key={idx}
                  onClick={() => {
                    if (cat === "Tutte") {
                      setAgeCategories(["Tutte"]);
                    } else {
                      if (ageCategories.includes("Tutte")) {
                        setAgeCategories([cat]);
                      } else if (active) {
                        setAgeCategories(ageCategories.filter(c => c !== cat));
                      } else {
                        setAgeCategories([...ageCategories, cat]);
                      }
                    }
                  }}
                  style={{
                    display: "inline-block",
                    margin: "4px",
                    padding: "4px 8px",
                    borderRadius: "12px",
                    backgroundColor: active ? "#28a745" : "#e0e0e0",
                    color: active ? "white" : "black",
                    cursor: "pointer"
                  }}
                >
                  {cat}
                </span>
              );
            })}
          </div>
        </div>
        <div className="mb-2">
          <label className="form-label"><strong>Categorie:</strong></label>
          <div>
            {["Tecnica", "Tattica", "Preparazione Atletica", "Tecnica del portiere", "Tutte"].map((cat, idx) => {
              const active = categories.includes(cat);
              return (
                <span
                  key={`cat-${idx}`}
                  onClick={() => {
                    if (cat === "Tutte") {
                      setCategories(["Tutte"]);
                    } else {
                      if (categories.includes("Tutte")) {
                        setCategories([cat]);
                      } else if (active) {
                        setCategories(categories.filter(c => c !== cat));
                      } else {
                        setCategories([...categories, cat]);
                      }
                    }
                  }}
                  style={{
                    display: "inline-block",
                    margin: "4px",
                    padding: "4px 8px",
                    borderRadius: "12px",
                    backgroundColor: active ? "#17a2b8" : "#e0e0e0",
                    color: active ? "white" : "black",
                    cursor: "pointer"
                  }}
                >
                  {cat}
                </span>
              );
            })}
          </div>
        </div>
        <div className="mb-2">
          <label className="form-label"><strong>Durata esercizio (minuti):</strong></label>
          <input
            type="number"
            className="form-control"
            value={duration}
            min="0"
            max="90"
            onChange={e => setDuration(Number(e.target.value))}
          />
        </div>
        <div className="mb-2">
          <label className="form-label"><strong>Complessità: {complexity}</strong></label>
          <input
            type="range"
            className="form-range"
            min="1"
            max="5"
            step="1"
            value={complexity}
            onChange={e => setComplexity(Number(e.target.value))}
          />
        </div>
      </div>

      <div className="card p-3 mb-3">
        <h5 className="mb-3">Campo</h5>
        {/* Goal Area Color and Label controls */}
        <div className="d-flex flex-row align-items-center gap-2 mt-2">
          {goalAreas.length > 0 && (
            <>
              <label className="form-label mb-0" style={{ fontWeight: 500 }}>Colore area goal:</label>
              <input
                type="color"
                value={
                  (() => {
                    // Convert rgba to hex if needed, fallback to #ffd700
                    const last = goalAreas[goalAreas.length - 1];
                    if (!last.color) return "#ffd700";
                    if (last.color.startsWith("#")) return last.color;
                    // Try to parse rgba string
                    const m = last.color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
                    if (m) {
                      const r = parseInt(m[1]), g = parseInt(m[2]), b = parseInt(m[3]);
                      return (
                        "#" +
                        [r, g, b]
                          .map((x) => x.toString(16).padStart(2, "0"))
                          .join("")
                      );
                    }
                    return "#ffd700";
                  })()
                }
                onChange={e => {
                  const hex = e.target.value;
                  setGoalAreas(prev => {
                    const updated = [...prev];
                    // Use rgba with alpha for fill, but store the hex for stroke
                    updated[updated.length - 1] = {
                      ...updated[updated.length - 1],
                      color: hex,
                    };
                    return updated;
                  });
                }}
                style={{ width: 36, height: 36, border: "none", background: "none", cursor: "pointer" }}
              />
              <label className="form-label mb-0 ms-3" style={{ fontWeight: 500 }}>Etichetta:</label>
              <input
                type="text"
                className="form-control"
                value={goalAreas[goalAreas.length - 1]?.label || ""}
                onChange={e => {
                  const val = e.target.value;
                  setGoalAreas(prev => {
                    const updated = [...prev];
                    updated[updated.length - 1] = {
                      ...updated[updated.length - 1],
                      label: val,
                    };
                    return updated;
                  });
                }}
                placeholder="Area label"
                style={{ maxWidth: 150 }}
              />
            </>
          )}
        </div>
        <svg ref={svgRef} viewBox="0 0 100 150" style={{ width: "100%", height: "500px", background: "#e6ffe6" }}>
          {/* Pitch background */}
          <rect x="2" y="2" width="96" height="146" fill="#7ac77a" stroke="#2e7d32" strokeWidth="0.5" />
          {/* Touchlines */}
          <rect x="5" y="5" width="90" height="140" fill="none" stroke="white" strokeWidth="0.8" />
          {/* Halfway line */}
          <line x1="5" y1="75" x2="95" y2="75" stroke="white" strokeWidth="0.6" />
          {/* Center circle & spot */}
          <circle cx="50" cy="75" r="12" fill="none" stroke="white" strokeWidth="0.6" />
          <circle cx="50" cy="75" r="0.7" fill="white" />
          {/* Penalty areas */}
          <rect x="30" y="5" width="40" height="12" fill="none" stroke="white" strokeWidth="0.6" />
          <rect x="30" y="133" width="40" height="12" fill="none" stroke="white" strokeWidth="0.6" />
         {/* Penalty spots */}
          <circle cx="50" cy="13" r="0.7" fill="white" />
          <circle cx="50" cy="137" r="0.7" fill="white" />
          {/* Penalty arcs */}
          <path d="M41,17 A9,9 0 0,0 59,17" stroke="white" strokeWidth="0.6" fill="none" />
          <path d="M41,133 A9,9 0 0,1 59,133" stroke="white" strokeWidth="0.6" fill="none" />
          {/* Goals */}
          <rect x="45" y="1" width="10" height="3" fill="none" stroke="white" strokeWidth="0.6" />
          <rect x="45" y="146" width="10" height="3" fill="none" stroke="white" strokeWidth="0.6" />
          {positions.map((p, i) => (
            <g
              key={i}
              onMouseDown={(e) => {
                e.preventDefault();
                const { x, y } = clientToSvg(svgRef.current, e.clientX, e.clientY);
                draggingIndex.current = i;
                draggingType.current = "position";

                const move = (ev) => {
                  const { x, y } = clientToSvg(svgRef.current, ev.clientX, ev.clientY);
                  // no offset correction, use raw coordinates
                  if (x < 5 || x > 95 || y < 5 || y > 145) {
                    setPositions(prev => prev.filter((_, idx) => idx !== draggingIndex.current));
                  } else {
                    setPositions(prev => {
                      const newPositions = [...prev];
                      newPositions[draggingIndex.current] = { ...newPositions[draggingIndex.current], x, y };
                      return newPositions;
                    });
                  }
                };
                const up = () => {
                  window.removeEventListener("mousemove", move);
                  window.removeEventListener("mouseup", up);
                };
                window.addEventListener("mousemove", move);
                window.addEventListener("mouseup", up);
              }}
              onDoubleClick={() => setPositions(positions.filter((_, idx) => idx !== i))}
              style={{ cursor: "grab" }}
            >
              {/* Invisible larger hit area for easier selection */}
              <circle cx={p.x} cy={p.y} r="6" fill="transparent" stroke="transparent" style={{ pointerEvents: "all" }} />
              {/* Visible marker */}
              <circle
                cx={p.x}
                cy={p.y}
                r="3"
                fill={p.type === "attacker" ? "blue" : p.type === "defender" ? "red" : "orange"}
              />
            </g>
          ))}
          {balls.map((b, i) => (
            <g
              key={`ball-${i}`}
              onMouseDown={(e) => {
                e.preventDefault();
                const { x, y } = clientToSvg(svgRef.current, e.clientX, e.clientY);
                draggingIndex.current = i;
                draggingType.current = "ball";

                const move = (ev) => {
                  const { x, y } = clientToSvg(svgRef.current, ev.clientX, ev.clientY);
                  // no offset correction, use raw coordinates
                  if (x < 5 || x > 95 || y < 5 || y > 145) {
                    setBalls(prev => prev.filter((_, idx) => idx !== draggingIndex.current));
                  } else {
                    setBalls(prev => {
                      const newBalls = [...prev];
                      newBalls[draggingIndex.current] = { x, y };
                      return newBalls;
                    });
                  }
                };
                const up = () => {
                  window.removeEventListener("mousemove", move);
                  window.removeEventListener("mouseup", up);
                };
                window.addEventListener("mousemove", move);
                window.addEventListener("mouseup", up);
              }}
              onDoubleClick={() => setBalls(balls.filter((_, idx) => idx !== i))}
              style={{ cursor: "grab" }}
            >
              {/* Invisible larger hit area */}
              <circle cx={b.x} cy={b.y} r="6" fill="transparent" stroke="transparent" style={{ pointerEvents: "all" }} />
              {/* Visible ball as emoji */}
              <text
                x={b.x}
                y={b.y}
                textAnchor="middle"
                dominantBaseline="middle"
                alignmentBaseline="middle"
                fontSize="5"
              >
                ⚽
              </text>
            </g>
          ))}
          {miniGoals.map((g, i) => (
            <g key={`goal-${i}`} style={{ cursor: "grab" }}
              onMouseDown={(e) => {
                e.preventDefault();
                draggingIndex.current = i;
                draggingType.current = "miniGoal";
                const move = (ev) => {
                  const { x, y } = clientToSvg(svgRef.current, ev.clientX, ev.clientY);
                  if (x < 5 || x > 95 || y < 5 || y > 145) {
                    setMiniGoals(prev => prev.filter((_, idx) => idx !== i));
                  } else {
                    setMiniGoals(prev => {
                      const newGoals = [...prev];
                      newGoals[i] = { x, y };
                      return newGoals;
                    });
                  }
                };
                const up = () => {
                  window.removeEventListener("mousemove", move);
                  window.removeEventListener("mouseup", up);
                };
                window.addEventListener("mousemove", move);
                window.addEventListener("mouseup", up);
              }}
              onDoubleClick={() => setMiniGoals(miniGoals.filter((_, idx) => idx !== i))}
            >
              <rect x={g.x-2} y={g.y-1} width="4" height="2" fill="white" stroke="black" strokeWidth="0.3"/>
            </g>
          ))}
          {cones.map((c, i) => (
            <g key={`cone-${i}`} onMouseDown={(e) => {
              e.preventDefault();
              draggingIndex.current = i;
              draggingType.current = "cone";
              const move = (ev) => {
                const { x, y } = clientToSvg(svgRef.current, ev.clientX, ev.clientY);
                if (x < 5 || x > 95 || y < 5 || y > 145) {
                  setCones(prev => prev.filter((_, idx) => idx !== i));
                } else {
                  setCones(prev => {
                    const newCones = [...prev];
                    newCones[i] = { x, y };
                    return newCones;
                  });
                }
              };
              const up = () => {
                window.removeEventListener("mousemove", move);
                window.removeEventListener("mouseup", up);
              };
              window.addEventListener("mousemove", move);
              window.addEventListener("mouseup", up);
            }}
            onDoubleClick={() => setCones(cones.filter((_, idx) => idx !== i))}
            style={{ cursor: "grab" }}>
              <polygon points={`${c.x},${c.y-2} ${c.x-1.5},${c.y+1.5} ${c.x+1.5},${c.y+1.5}`} fill="orange" />
            </g>
          ))}
          {markers.map((m, i) => (
            <g key={`marker-${i}`} onMouseDown={(e) => {
              e.preventDefault();
              draggingIndex.current = i;
              draggingType.current = "marker";
              const move = (ev) => {
                const { x, y } = clientToSvg(svgRef.current, ev.clientX, ev.clientY);
                if (x < 5 || x > 95 || y < 5 || y > 145) {
                  setMarkers(prev => prev.filter((_, idx) => idx !== i));
                } else {
                  setMarkers(prev => {
                    const newMarkers = [...prev];
                    newMarkers[i] = { x, y };
                    return newMarkers;
                  });
                }
              };
              const up = () => {
                window.removeEventListener("mousemove", move);
                window.removeEventListener("mouseup", up);
              };
              window.addEventListener("mousemove", move);
              window.addEventListener("mouseup", up);
            }}
            onDoubleClick={() => setMarkers(markers.filter((_, idx) => idx !== i))}
            style={{ cursor: "grab" }}>
              <circle cx={m.x} cy={m.y} r="1.2" fill="yellow" stroke="black" strokeWidth="0.2"/>
            </g>
          ))}
          {/* Goal Areas */}
          {goalAreas.map((area, i) => (
            <g
              key={`goalarea-${i}`}
              style={{ cursor: "grab" }}
              onMouseDown={e => {
                e.preventDefault();
                draggingIndex.current = i;
                draggingType.current = "goalArea";
                const start = clientToSvg(svgRef.current, e.clientX, e.clientY);
                const move = (ev) => {
                  const { x, y } = clientToSvg(svgRef.current, ev.clientX, ev.clientY);
                  let newX = x, newY = y;
                  newX = Math.max(5, Math.min(95 - (area.width || 10), newX));
                  newY = Math.max(5, Math.min(145 - (area.height || 5), newY));
                  setGoalAreas(prev => {
                    const updated = [...prev];
                    updated[i] = {
                      ...updated[i],
                      x: newX,
                      y: newY,
                      width: area.width || 10,
                      height: area.height || 5,
                    };
                    return updated;
                  });
                };
                const up = () => {
                  window.removeEventListener("mousemove", move);
                  window.removeEventListener("mouseup", up);
                };
                window.addEventListener("mousemove", move);
                window.addEventListener("mouseup", up);
              }}
              onDoubleClick={() => setGoalAreas(goalAreas.filter((_, idx) => idx !== i))}
            >
              <rect
                x={area.x}
                y={area.y}
                width={area.width || 10}
                height={area.height || 5}
                fill={area.color || "rgba(255, 215, 0, 0.4)"}
                stroke={area.color || "#FFD700"}
                strokeWidth="0.5"
              />
              {/* Label in the center of the area, if provided */}
              {area.label && (
                <text
                  x={area.x + (area.width || 10) / 2}
                  y={area.y + (area.height || 5) / 2}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  alignmentBaseline="middle"
                  fontSize={Math.max(2, Math.min((area.height || 5), (area.width || 10)) / 2)}
                  fill="#333"
                  pointerEvents="none"
                  style={{ userSelect: "none" }}
                >
                  {area.label}
                </text>
              )}
              {/* Resize handles */}
              {[
                { cx: area.x, cy: area.y }, // top-left
                { cx: area.x + (area.width || 10), cy: area.y }, // top-right
                { cx: area.x, cy: area.y + (area.height || 5) }, // bottom-left
                { cx: area.x + (area.width || 10), cy: area.y + (area.height || 5) }, // bottom-right
              ].map((handle, hIdx) => (
                <rect
                  key={`handle-${hIdx}`}
                  x={handle.cx - 1.5}
                  y={handle.cy - 1.5}
                  width={3}
                  height={3}
                  fill="black"
                  style={{ cursor: "nwse-resize" }}
                  onMouseDown={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    const start = clientToSvg(svgRef.current, e.clientX, e.clientY);
                    const orig = { ...area };
                    const move = (ev) => {
                      const { x, y } = clientToSvg(svgRef.current, ev.clientX, ev.clientY);
                      let newWidth = orig.width || 10;
                      let newHeight = orig.height || 5;
                      let newX = orig.x;
                      let newY = orig.y;

                      if (hIdx === 0) { // top-left
                        newWidth = orig.width + (orig.x - x);
                        newHeight = orig.height + (orig.y - y);
                        newX = x;
                        newY = y;
                      } else if (hIdx === 1) { // top-right
                        newWidth = x - orig.x;
                        newHeight = orig.height + (orig.y - y);
                        newY = y;
                      } else if (hIdx === 2) { // bottom-left
                        newWidth = orig.width + (orig.x - x);
                        newHeight = y - orig.y;
                        newX = x;
                      } else if (hIdx === 3) { // bottom-right
                        newWidth = x - orig.x;
                        newHeight = y - orig.y;
                      }

                      newWidth = Math.max(5, Math.min(90, newWidth));
                      newHeight = Math.max(3, Math.min(140, newHeight));

                      setGoalAreas(prev => {
                        const updated = [...prev];
                        updated[i] = { ...updated[i], x: newX, y: newY, width: newWidth, height: newHeight };
                        return updated;
                      });
                    };
                    const up = () => {
                      window.removeEventListener("mousemove", move);
                      window.removeEventListener("mouseup", up);
                    };
                    window.addEventListener("mousemove", move);
                    window.addEventListener("mouseup", up);
                  }}
                />
              ))}
            </g>
          ))}
        </svg>

        <div className="d-flex flex-wrap align-items-center gap-2">
          <button className="btn btn-outline-dark" onClick={() => setBalls([...balls, { x: 50, y: 100 }])}>⚽ Pallone</button>
          <button className="btn btn-outline-danger" onClick={() => setBalls([])}>🗑️ Rimuovi tutti</button>
          <button
            className="btn btn-outline-warning"
            onClick={() => setPositions([...positions, { type: "jolly", x: 50, y: 100 }])}
          >
            🟠 Jolly
          </button>
          <button
            className="btn btn-outline-primary"
            onClick={() => setPositions([...positions, { type: "attacker", x: 50, y: 100 }])}
          >
            🔵 Attaccante
          </button>
          <button
            className="btn btn-outline-danger"
            onClick={() => setPositions([...positions, { type: "defender", x: 50, y: 100 }])}
          >
            🔴 Difensore
          </button>
          <button className="btn btn-outline-secondary" onClick={() => setMiniGoals([...miniGoals, { x: 50, y: 20 }])}>🥅 Porticina</button>
          <button className="btn btn-outline-warning" onClick={() => setCones([...cones, { x: 50, y: 50 }])}>🔺 Cono</button>
          <button className="btn btn-outline-info" onClick={() => setMarkers([...markers, { x: 50, y: 80 }])}>⭕ Cinesino</button>
          <button
            className="btn btn-outline-warning"
            onClick={() =>
              setGoalAreas([
                ...goalAreas,
                { x: 30, y: 60, width: 30, height: 20, color: "rgba(255, 0, 0, 0.5)", label: "AREA" }
              ])
            }
          >
            🟨 Area goal
          </button>
        </div>
      </div>

      <div className="mt-4 text-center">
        <button className="btn btn-primary me-2" onClick={saveOverwrite}>💾 Salva</button>
        <button className="btn btn-success me-2" onClick={saveNew}>➕ Salva come nuova</button>
        <button className="btn btn-danger" onClick={deleteDrill}>🗑️ Elimina</button>
      </div>
      {saveMessage && (
        <div className="alert alert-success mt-3" role="alert">
          {saveMessage}
        </div>
      )}
    </div>
  );
}

export default DrillDetail;